import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import UserWishlistInformations from '../../components/UserWishlist/UserWishlistInformations'
import UserWishlistProductsItems from '../../components/UserWishlist/UserWishlistProductsItems'
import ModalMessage from '../../components/ModalMessage'
import Footer from '../../components/FooterButton'

import { ReactComponent as ShareIcon } from '../../assets/ShareIcon.svg'
import SendGTAGEvent from '../../utils/sendGTAGEvent'

import useWishList from '../../hooks/WishListContext'

import MaisConteudoAPi from '../../services/MaisConteudoAPi'

import { Container, UserWishlistInfoContainer, FooterButtonContent } from './styles'
interface IinfoState {
    pageViews: number;
    sales: number;
    conversionRate: number;
    amountSales: number;
    cachedAt: Date;
}

interface IWishlistState {
    environment: string;
    name: string;
    description: string;
    createdAt: string;
    products: {
        items: {
            images: {
                imageUrl: string;
            }[]

            sellers: {
                sellerName: string;
                commertialOffer: {
                    Price: number
                };
            }[]
        }[];
        linkText: string;
        productName: string;
        productReference: string;
        productId: string;
    }[]
}

interface ILoadingState {
    infosLoading: boolean;
    itensLoading: boolean;
}

interface IErrorState {
    shouldDisabledButtons: boolean;
    error: string;
    isToShow: boolean;
}

const UserWishList: React.FC = () => {

    const mounted = useRef(false)

    const { linkName, code, linkId, environment } = useParams()

    const navigate = useNavigate()

    const { onShareLinkHandler, onShareLinkWithParamsHandler } = useWishList()

    const [loading, setLoading] = useState<ILoadingState>({ infosLoading: false, itensLoading: false })
    const [isEmpty, setIsEmpty] = useState<boolean>(false)
    const [warningModalIsOpen, setWarningModalIsOpen] = useState<boolean>(false)
    const [infos, setInfos] = useState<IinfoState>({
        pageViews: 0,
        sales: 0,
        conversionRate: 0,
        amountSales: 0,
        cachedAt: new Date()
    })

    const [wishList, setWishList] = useState<IWishlistState>({
        createdAt: '',
        name: '',
        description: '',
        environment: '',
        products: []
    })
    const [error, setError] = useState<IErrorState>({ error: '', isToShow: false, shouldDisabledButtons: false })

    const footerButtonContentMemo = useMemo(() => (
        <FooterButtonContent>
            <ShareIcon />

            <span>
                Compartilhar
            </span>
        </FooterButtonContent>
    ), [])

    const onFetchInfoHandler = useCallback(async (noCache = false) => {
        try {
            setLoading(prevState => ({ ...prevState, infosLoading: true }))
            let res
            const paramsCache = noCache ? `${environment}?noCache=true` : `${environment}`

            if (!linkName) {
                res = await MaisConteudoAPi.get(`v1/wishlists/${linkId}/info/${paramsCache}`)
            } else {
                res = await MaisConteudoAPi.get(`v1/wishlists/${linkId}/info/${linkName}/${code}/${paramsCache}`)
            }

            if (mounted.current) {
                setLoading(prevState => ({ ...prevState, infosLoading: false }))
                const date = new Date(res.data.cachedAt)
                setInfos({ ...res.data, cachedAt: date })
            }

        } catch (error: any) {
            setLoading(prevState => ({ ...prevState, infosLoading: false }))
            setError({
                error: error.response?.data.message || 'Algo de errado aconteceu, por favor tente novamente!',
                isToShow: true,
                shouldDisabledButtons: true
            })
        }
    }, [linkName, environment, linkId, code])

    const onFetchWishlistHandler = useCallback(async () => {
        try {
            setLoading(prevState => ({ ...prevState, itensLoading: true }))

            const res = await MaisConteudoAPi.get(`v1/wishlists/${linkId}/browse`)
            if (mounted.current) {
                setLoading(prevState => ({ ...prevState, itensLoading: false }))
                setWishList(res.data)

                if (res.data.products.length <= 0) setIsEmpty(true)
            }

        } catch (error: any) {
            setLoading(prevState => ({ ...prevState, itensLoading: false }))
            setError({
                error: error.response?.data.message || 'Algo de errado aconteceu, por favor tente novamente!',
                isToShow: true,
                shouldDisabledButtons: true
            })
        }
    }, [linkId])

    const onClickRemoveButtonHandler = useCallback(async (linkText: string) => {
        try {
            setLoading(prevState => ({ ...prevState, itensLoading: true }))

            await MaisConteudoAPi.post(`/v1/wishlists/${linkId}/remove`, { products: [linkText] })
            setLoading(prevState => ({ ...prevState, itensLoading: false }))
            setWishList(prevState => {
                const newProducts = prevState.products.filter(product => product.linkText !== linkText)

                if (newProducts.length <= 0) setIsEmpty(true)

                return {
                    ...prevState,
                    products: newProducts
                }
            })

        } catch (error: any) {
            setLoading(prevState => ({ ...prevState, itensLoading: false }))
            setError({
                error: error.response?.data.message || 'Algo de errado aconteceu, por favor tente novamente!',
                isToShow: true,
                shouldDisabledButtons: false
            })
        }
    }, [linkId])

    const onCloseWarningModalHandler = useCallback(() => setWarningModalIsOpen(false), [])

    const onOpenWarningModalHandler = useCallback(() => setWarningModalIsOpen(true), [])

    const onClickEditButtonHandler = useCallback(() => navigate(`/edit-wishlist/${linkId}`), [linkId, navigate])

    const onCloseErrorHandler = useCallback(() => setError(prevState => ({ ...prevState, isToShow: false })), [])

    const onClickActionWarningModalHandler = useCallback(async () => {
        try {
            await MaisConteudoAPi.delete(`/v1/wishlists/${linkId}`)
            navigate('/my-wishlists')
        } catch (error: any) {
            onCloseWarningModalHandler()
            setError({
                error: error.response?.data.message || 'Algo de errado aconteceu, por favor tente novamente!',
                isToShow: true,
                shouldDisabledButtons: false
            })
        }
    }, [linkId, navigate, onCloseWarningModalHandler])

    const onChooseShareLinkHandler = useCallback(() => {
        if (linkName) {
            onShareLinkWithParamsHandler(wishList.name, linkName, code, linkId)
        } else {
            onShareLinkHandler(wishList.name, linkId)
        }
    }, [code, linkId, linkName, onShareLinkHandler, onShareLinkWithParamsHandler, wishList.name])

    const infoWarningMessageModal = useMemo(() => (
        {
            title: 'Tem certeza ?',
            description: `Excluir a lojinha ${wishList.name} é um processo irreversível!`,
            onclick: onClickActionWarningModalHandler,
        }
    ), [wishList.name, onClickActionWarningModalHandler])

    useEffect(() => {
        mounted.current = true
        onFetchWishlistHandler()
        return () => { mounted.current = false }
    }, [onFetchWishlistHandler])

    useEffect(() => {
        mounted.current = true
        onFetchInfoHandler()
        return () => { mounted.current = false }
    }, [onFetchInfoHandler])

    return (
        <Container>
            <ModalMessage
                optionSVG="warningImage"
                isOpen={warningModalIsOpen}
                onClose={onCloseWarningModalHandler}
                tittle={infoWarningMessageModal.title}
                textWishList={infoWarningMessageModal.description}
                onClick={infoWarningMessageModal.onclick}
            />
            <ModalMessage
                optionSVG="errorImage"
                isOpen={error.isToShow}
                onClose={onCloseErrorHandler}
                tittle='Oops...'
                textWishList={error.error}
            />
            <UserWishlistInfoContainer>
                <UserWishlistInformations
                    {...Object.assign(wishList, infos)}
                    error={error.shouldDisabledButtons}
                    onClickDeleteButton={onOpenWarningModalHandler}
                    onClickEditButton={onClickEditButtonHandler}
                    onClickUpdateButton={onFetchInfoHandler}
                    loading={loading}
                />
                <UserWishlistProductsItems
                    products={wishList.products}
                    onClickRemoveButtonHandler={onClickRemoveButtonHandler}
                    isEmpty={isEmpty}
                    loading={loading.itensLoading}
                />
            </UserWishlistInfoContainer>
            <Footer
                name={footerButtonContentMemo}
                onClick={() => {
                    onChooseShareLinkHandler()
                    SendGTAGEvent(
                        'compartilha_lojinha',
                        'Lojinha',
                        'Clica em compartilhar lojinha',
                        linkId
                    )
                }}
                disabled={error.shouldDisabledButtons}
            />
        </Container>
    )
}


export default UserWishList